import { cn } from 'lib/utils'
import type React from 'react'

interface FlagProps {
  url?: string
  iso?: string
  size?: number
  circle?: boolean
  className?: string
  borderRadius?: number
}

export const Flag: React.FC<FlagProps> = ({
  url = '/flags.svg',
  iso = 'US',
  size = 30,
  circle,
  className,
  borderRadius = 4,
}) => {
  const shouldClip = iso !== 'unknown'
  const renderSize = shouldClip ? (circle ? (size * 3) / 2 : size * 1.33 - 2) : size
  const clipId = `clip-${size}`
  const dimensions = {
    size,
    radius: renderSize / 3,
    center: renderSize / 2,
    width: renderSize,
    height: circle ? renderSize : renderSize * 0.75 - 2,
    margin: shouldClip ? `0 -${renderSize / 6}px` : '',
    borderRadius: (borderRadius * renderSize) / size,
  }
  return (
    <div
      style={{ display: 'inline-flex', margin: dimensions.margin }}
      className={cn('neo-flag', className)}
    >
      <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        width={renderSize}
        height={circle ? renderSize : dimensions.height}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Flag of ${iso}`}
      >
        {shouldClip && (
          <clipPath id={clipId}>
            {circle ? (
              <circle r={dimensions.radius} cx={dimensions.center} cy={dimensions.center} />
            ) : (
              <rect width="100%" height="100%" rx={borderRadius} ry={borderRadius} x="0" y="0" />
            )}
          </clipPath>
        )}
        <use
          href={`${url}#flag-${iso.toLowerCase()}`}
          clipPath={shouldClip ? `url(#${clipId})` : ''}
        />
      </svg>
    </div>
  )
}

interface IconProps {
  className?: string
  name?: string
  size?: number
  svg?: React.ComponentType
}

export const Icon: React.FC<IconProps> = ({
  name = undefined,
  size,
  svg: SvgIcon,
  className,
  ...props
}) => (
  <span
    style={{
      fontSize: size,
      lineHeight: 1,
      width: size,
      height: size,
    }}
    className={cn('neo-icon flex items-center justify-center', className)}
    {...props}
  >
    {SvgIcon ? <SvgIcon /> : name}
  </span>
)
